import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * StagingAccessGate
 *
 * Local-dev-only gate that runs a one-shot probe of the staging API on mount.
 * Two distinct dev-time failure modes both surface as a "CORS error" in
 * DevTools, and the gate's job is to tell them apart and render the right
 * remediation modal instead of a wall of red text:
 *
 *   1. **Access cookie missing.** The developer is on an Access-allowed origin
 *      (e.g. `localhost:3000`) but hasn't completed the Cloudflare Access
 *      login flow for this staging hostname yet, so credentialed calls 302
 *      to the login page. Fix: open `/v1/health` in a new tab to trigger the
 *      OTP flow, then reload.
 *
 *   2. **Origin not in Access "Allowed origins".** The dev server is being
 *      hit via a tunnel hostname (ngrok / Cloudflare Tunnel quick tunnel /
 *      LAN IP) that isn't in the staging Access app's CORS allowlist, so the
 *      preflight is rejected at the edge before the request ever reaches the
 *      worker. Fix: add the current origin to the Zero Trust app's "Allowed
 *      origins" list (and to `ALLOWED_ORIGIN_PATTERNS` in
 *      `production/api/src/index.ts` if the worker doesn't already match it).
 *
 * Detection trick: we issue a single deliberately-preflighted GET to
 * `/v1/health` with `redirect: "manual"`. That gives us a clean three-way
 * fork on the response:
 *
 *   TypeError       → preflight rejected → origin not in Access CORS list
 *   opaqueredirect  → preflight passed, real request 302'd → cookie missing
 *   res.ok          → both fine, render the app
 *
 * Skip conditions (rendered as a no-op pass-through):
 * - Bundle is a production build (deployed previews, prod viewer)
 * - Viewer is not targeting a staging slot (e.g. `apiTarget === "production"`
 *   or `"local"` for wrangler dev)
 *
 * Tunneled dev (ngrok / Cloudflare Tunnel / LAN IP for phone testing) is
 * intentionally NOT a skip condition — that's exactly the case the
 * `access-cors-not-allowed` branch was added for.
 *
 * Mid-session expiry: this gate only checks at boot. If the cookie expires
 * mid-session, the user will see CORS errors until they reload — at which
 * point this gate catches the new state and shows the modal again. That
 * trade-off is acceptable for a 24h cookie lifetime.
 */
import React from "react";
import { API_BASE_URL, getStagingSlot, isViewerDevBuild, } from "@/viewer-config";
import "./staging-access-gate.scss";
// 5s is generous for a localhost → Cloudflare edge round-trip but still bails
// reasonably fast if the network is genuinely down (in which case we let the
// app render and the per-request error paths handle it).
const PROBE_TIMEOUT_MS = 5000;
/**
 * Probe the staging API's health endpoint to distinguish the two dev-time
 * Access failure modes. Two settings carry the diagnosis:
 *
 *   - `Authorization` header — forces the browser to issue a CORS preflight
 *     (a "simple" GET would skip preflight entirely and miss the
 *     `access-cors-not-allowed` case). The value is a dummy; `/v1/health`
 *     is a public route on the worker so it isn't validated.
 *   - `redirect: "manual"` — captures the Access login 302 as an
 *     `opaqueredirect` response instead of letting fetch try to follow it
 *     cross-origin to `puttyco.cloudflareaccess.com` (which would TypeError
 *     and collide with the preflight-failure signal).
 *
 * The fork:
 *
 *   TypeError on the awaited fetch        → preflight rejected by Access at
 *                                           the edge → origin not in the
 *                                           Access "Allowed origins" CORS
 *                                           list. Show the CORS-add modal.
 *   res.type === "opaqueredirect"         → preflight passed but the real
 *                                           request 302'd to the Access
 *                                           login page → cookie missing.
 *                                           Show the login modal.
 *   res.ok                                → both fine, render the app.
 *   anything else                         → indeterminate; let the app
 *                                           render and per-request error
 *                                           paths take over.
 */
async function probeStagingAccess() {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), PROBE_TIMEOUT_MS);
    try {
        const res = await fetch(`${API_BASE_URL}/v1/health`, {
            method: "GET",
            credentials: "include",
            headers: { Authorization: "Bearer staging-access-probe" },
            redirect: "manual",
            signal: ctrl.signal,
        });
        if (res.type === "opaqueredirect") {
            return { type: "access-login-required" };
        }
        // Any HTTP response (including 4xx/5xx) means the preflight passed and
        // the cookie was accepted. Don't show a modal for those — the app's
        // normal error UI will surface them.
        return res.ok ? { type: "ok" } : { type: "indeterminate" };
    }
    catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
            return { type: "indeterminate" };
        }
        // A TypeError on a preflighted credentialed fetch with `redirect: "manual"`
        // narrows to one cause: Access rejected the OPTIONS preflight because the
        // current origin isn't in its "Allowed origins" list. The cookie-missing
        // case is already captured by `opaqueredirect` above, so any TypeError
        // here is the CORS allowlist case.
        if (err instanceof TypeError) {
            return { type: "access-cors-not-allowed" };
        }
        return { type: "indeterminate" };
    }
    finally {
        clearTimeout(timer);
    }
}
function probeResultToState(result) {
    switch (result.type) {
        case "access-login-required":
            return "needs-login";
        case "access-cors-not-allowed":
            return "needs-cors-allow";
        case "ok":
        case "indeterminate":
            // Indeterminate falls through to "ok" on purpose: better to let the
            // app render and let per-request error paths run than to block boot
            // on a possibly-transient probe failure.
            return "ok";
    }
}
export const StagingAccessGate = ({ children, }) => {
    const slot = getStagingSlot();
    const shouldGate = isViewerDevBuild() && slot !== undefined;
    const [state, setState] = React.useState(() => shouldGate ? "checking" : "ok");
    React.useEffect(() => {
        if (!shouldGate)
            return;
        if (state !== "checking")
            return;
        let cancelled = false;
        void probeStagingAccess().then((result) => {
            if (cancelled)
                return;
            setState(probeResultToState(result));
        });
        return () => {
            cancelled = true;
        };
    }, [shouldGate, state]);
    if (state === "checking" && slot) {
        return _jsx(StagingAccessProbeLoading, { slot: slot });
    }
    if (state === "needs-login" && slot) {
        return _jsx(StagingAccessLoginPrompt, { slot: slot });
    }
    if (state === "needs-cors-allow" && slot) {
        return _jsx(StagingCorsNotAllowedPrompt, { slot: slot });
    }
    return _jsx(_Fragment, { children: children });
};
// ---------------------------------------------------------------------------
// Visual states
// ---------------------------------------------------------------------------
const StagingAccessProbeLoading = ({ slot, }) => (_jsx("div", { className: "staging-access-gate__loading", children: _jsxs("p", { children: ["Checking Cloudflare Access for ", slot, "\u2026"] }) }));
const StagingAccessLoginPrompt = ({ slot, }) => {
    const healthUrl = `${API_BASE_URL}/v1/health`;
    return (_jsx("div", { className: "staging-access-gate__backdrop", children: _jsxs("div", { className: `staging-access-gate staging-access-gate--${slot}`, children: [_jsx("div", { className: "staging-access-gate__chip", children: slot.toUpperCase() }), _jsx("h2", { className: "staging-access-gate__title", children: "Log into Cloudflare Access" }), _jsxs("p", { className: "staging-access-gate__body", children: ["The ", _jsx("code", { children: slot }), " API is gated by Cloudflare Zero Trust. Open the link below in a new tab, complete the Access login (email OTP), then come back and reload this page."] }), _jsxs("a", { className: "staging-access-gate__link", href: healthUrl, target: "_blank", rel: "noopener noreferrer", children: ["Open ", healthUrl, " \u2197"] }), _jsx("button", { className: "staging-access-gate__reload", onClick: () => window.location.reload(), children: "I've logged in \u2014 reload" }), _jsxs("p", { className: "staging-access-gate__hint", children: ["If the link still bounces back here, check that third-party cookies are allowed for ", _jsx("code", { children: "[*.]putty.zone" }), " (Brave: lower Shields on", " ", _jsx("code", { children: "localhost" }), ")."] })] }) }));
};
// Top-level Zero Trust dashboard URL. Cloudflare doesn't expose stable
// deep-links to a specific Access app's CORS panel, so we drop the user one
// click short — they navigate Access → Applications → <staging slot> from
// there. Hardcoded rather than constructed so a typo'd account-id doesn't
// surface as a broken link at the worst possible moment.
const ZERO_TRUST_DASHBOARD_URL = "https://one.dash.cloudflare.com/";
const StagingCorsNotAllowedPrompt = ({ slot, }) => {
    // `window` is always defined here — the gate is mounted under a browser
    // entrypoint and the indeterminate/SSR cases are filtered out upstream by
    // `isViewerDevBuild()` and the `state === "needs-cors-allow"` guard. Fall
    // back to a placeholder anyway so the JSX doesn't crash if that invariant
    // ever breaks; the user-facing copy still tells them what to do.
    const origin = typeof window !== "undefined" ? window.location.origin : "(unknown origin)";
    return (_jsx("div", { className: "staging-access-gate__backdrop", children: _jsxs("div", { className: `staging-access-gate staging-access-gate--${slot}`, children: [_jsx("div", { className: "staging-access-gate__chip", children: slot.toUpperCase() }), _jsx("h2", { className: "staging-access-gate__title", children: "Dev origin blocked by Cloudflare Access" }), _jsxs("p", { className: "staging-access-gate__body", children: ["Your current origin ", _jsx("code", { children: origin }), " isn't in the", " ", _jsx("code", { children: slot }), " Cloudflare Access \"Allowed origins\" CORS list, so every credentialed call to the API is failing its preflight at the edge \u2014 before it ever reaches the worker."] }), _jsx("a", { className: "staging-access-gate__link", href: ZERO_TRUST_DASHBOARD_URL, target: "_blank", rel: "noopener noreferrer", children: "Open Cloudflare Zero Trust \u2197" }), _jsx("button", { className: "staging-access-gate__reload", onClick: () => window.location.reload(), children: "I've added the origin \u2014 reload" }), _jsxs("p", { className: "staging-access-gate__hint", children: ["In the dashboard: Access \u2192 Applications \u2192 the app covering", " ", _jsxs("code", { children: ["staging-api-", slot.replace(/^staging-/, ""), ".putty.zone"] }), " \u2192 CORS settings \u2192 \"Allowed origins\" \u2192 add ", _jsx("code", { children: origin }), " ", "verbatim. The worker's ", _jsx("code", { children: "ALLOWED_ORIGIN_PATTERNS" }), " in", " ", _jsx("code", { children: "production/api/src/index.ts" }), " needs to match the same shape; if you've just added a new pattern there, redeploy the worker before reloading."] })] }) }));
};
